import { ODSArrowUp } from '@telekom-ods/react-icons';
import { ODSButton } from '@telekom-ods/react-ui-kit';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import styles from './ScrollToTop.module.css';

interface ScrollToTopProps {
  scrollThreshold?: number;
  forceVisible?: boolean;
  scrollContainerClass?: string;
  fixed?: boolean;
  className?: string;
}

export const ScrollToTop = ({
  scrollThreshold = 0.2,
  forceVisible = false,
  scrollContainerClass,
  fixed = true,
  className = '',
}: ScrollToTopProps): React.JSX.Element => {
  const intl = useIntl();
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    if (forceVisible) {
      setIsVisible(true);
      return;
    }

    const scrollContainer: Window | HTMLElement = scrollContainerClass
      ? (() => {
          const element = document.querySelector(`.${scrollContainerClass}`);
          return element instanceof HTMLElement ? element : window;
        })()
      : window;

    const handleScroll = (): void => {
      const isWindow = scrollContainer === window;

      let scrollTop = 0;
      if (isWindow) {
        scrollTop = window.scrollY;
      } else if (scrollContainer instanceof HTMLElement) {
        scrollTop = scrollContainer.scrollTop;
      }

      let scrollHeight = 0;
      if (isWindow) {
        scrollHeight = document.documentElement.scrollHeight;
      } else if (scrollContainer instanceof HTMLElement) {
        scrollHeight = scrollContainer.scrollHeight;
      }

      const clientHeight = isWindow
        ? document.documentElement.clientHeight
        : (scrollContainer as HTMLElement).clientHeight;

      const scrolledPercentage = scrollTop / (scrollHeight - clientHeight);

      setIsVisible(scrolledPercentage > scrollThreshold);
    };

    const addScrollListener = (): void => {
      if (scrollContainer === window) {
        window.addEventListener('scroll', handleScroll);
      } else {
        scrollContainer.addEventListener('scroll', handleScroll);
      }
    };

    const removeScrollListener = (): void => {
      if (scrollContainer === window) {
        window.removeEventListener('scroll', handleScroll);
      } else {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };

    addScrollListener();
    // run once to set initial state if page is already scrolled
    handleScroll();

    return () => {
      removeScrollListener();
    };
  }, [scrollThreshold, forceVisible, scrollContainerClass]);

  const handleScrollToTop = (): void => {
    const scrollContainer: Window | HTMLElement = scrollContainerClass
      ? (() => {
          const element = document.querySelector(`.${scrollContainerClass}`);
          return element instanceof HTMLElement ? element : window;
        })()
      : window;

    if (scrollContainer === window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <ODSButton
      buttonIcon={<ODSArrowUp />}
      buttonType='iconOnly'
      variant='outline'
      size='small'
      onClick={handleScrollToTop}
      className={[
        className,
        styles.scrollToTop,
        fixed ? styles.fixed_style : styles.absolute_style,
        isVisible ? styles.visible : styles.hidden,
        isVisible && styles.pulsating,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={intl.formatMessage({ id: 'scrollToTop' })}
    />
  );
};
